import React from 'react';
import { useHistory } from 'react-router-dom';
import { Tab, Segment, Button, Modal } from 'semantic-ui-react';
import { UniversalLink } from '@plone/volto/components';
import { flattenToAppURL } from '@plone/volto/helpers';
import TreeGraph from './TreeGraph';
import './DocumentTabs.less';

/**
 * Related / History / … tab panel beside the metadata table, fed by the
 * backend `document_relations` expander (legacy backReferences port). The
 * History tab carries the "Show All" relationship-graph modal when the
 * supersedes/cancels chain has more than the current document in it.
 *
 * Legacy item format preserved: "docid, title" linked, "on <date>" suffix,
 * red "Private" tag for private targets, plain text for unlinked entries.
 * String references now resolve to real links across the unified platform
 * (backend urlmap), so cross-site items navigate like any other link.
 */

function entryHasContent(entry) {
  return (entry.value || []).length > 0;
}

function groupHasContent(entries) {
  return (entries || []).some(entryHasContent);
}

const RelationItem = ({ item }) => {
  const label = item.docid ? `${item.docid}, ${item.title}` : item.title;
  return (
    <Segment className="doc-rel-item">
      {item.url && item.review_state !== 'private' ? (
        <UniversalLink
          href={flattenToAppURL(item.url)}
          className="doc-rel-title"
        >
          {label}
        </UniversalLink>
      ) : (
        // A private target is named but NOT linked. Anonymous readers get 410
        // there (BL-0041), so a live href promises a page that does not exist
        // for them -- 92 published pages carried 94 such pointers into 80
        // private objects [private_reachability.py, 2026-08-16]. The relation
        // itself is data and stays visible: title, date and the red Private
        // pill all still render, which is what BL-0006 asked be preserved.
        <span className="doc-rel-title">{label}</span>
      )}
      {item.date ? (
        <span className="doc-rel-date"> on {item.date}</span>
      ) : null}
      {item.review_state === 'private' ? (
        <span className="prc-pill prc-pill--private doc-rel-private">Private</span>
      ) : null}
    </Segment>
  );
};

const GroupPane = ({ entries, graph, docTitle, statusCls }) => {
  const [open, setOpen] = React.useState(false);
  const history = useHistory();
  const showGraph = graph && (graph.nodes || []).length > 1;

  const onNodeClick = (node) => {
    if (!node.url || node.mark) return;
    setOpen(false);
    const to = flattenToAppURL(node.url);
    if (/^https?:\/\//.test(to)) {
      window.location.href = to;
    } else {
      history.push(to);
    }
  };

  return (
    <Tab.Pane attached={false} className={`doc-rel-pane ${statusCls || ''}`}>
      {entries.filter(entryHasContent).map((entry, i) => (
        <div className="doc-rel-group" key={i}>
          {/* h2, not h3: the page <h1> is the document title and
              nothing sits between them, so h3 skipped a level
              (TAIUIT item 9). Classed rather than styled by tag --
              the stylesheets targeted h4 against an h3 in the markup
              and had silently matched nothing. */}
          {entry.title ? (
            <h2 className="doc-rel-group__h">{entry.title}:</h2>
          ) : null}
          {entry.value.map((item, j) => (
            <RelationItem item={item} key={j} />
          ))}
        </div>
      ))}
      {showGraph ? (
        <>
          <Button
            className="doc-graph-button"
            onClick={() => setOpen(true)}
            size="small"
          >
            Show All
          </Button>
          <Modal
            open={open}
            onClose={() => setOpen(false)}
            size="large"
            className="doc-graph-modal"
            closeIcon
          >
            <Modal.Header>{docTitle}</Modal.Header>
            <Modal.Content>
              <p className="doc-graph-hint">
                Scroll and Zoom for details. Click on Nodes to View Document.
              </p>
              <TreeGraph
                nodes={graph.nodes}
                edges={graph.edges}
                onNodeClick={onNodeClick}
              />
            </Modal.Content>
            <Modal.Actions>
              <Button color="black" onClick={() => setOpen(false)}>
                Close
              </Button>
            </Modal.Actions>
          </Modal>
        </>
      ) : null}
    </Tab.Pane>
  );
};

// The auto-detected glossary backlinks were a pane here; they are now a flat,
// server-rendered section on the document page (DocumentView/AutoReferences),
// because a tab pane hydrates client-side and a crawler never saw it.

const DocumentTabs = ({ content }) => {
  const relations = content['@components']?.document_relations;
  const order = relations?.order || [];
  if (order.length === 0) return null;

  const statusTitle =
    content.status && (content.status.title || content.status.token);
  const statusCls = statusTitle
    ? `status-${String(statusTitle).toLowerCase().replace(/[^a-z0-9]+/g, '-')}`
    : '';

  const panes = order.map((group) => {
    const entries = relations.items?.[group] || [];
    const hasContent =
      groupHasContent(entries) ||
      (group === 'history' && (relations.graph?.nodes || []).length > 1);
    return {
      menuItem: group.charAt(0).toUpperCase() + group.slice(1),
      render: () => (
        <GroupPane
          entries={entries}
          graph={group === 'history' ? relations.graph : null}
          docTitle={content.title}
          statusCls={statusCls}
        />
      ),
      hasContent,
    };
  });

  // legacy behavior: the tab bar always renders; empty groups are just
  // disabled, and with nothing to show no pane is active (index -1)
  const defaultActiveIndex = panes.findIndex((p) => p.hasContent);

  return (
    <Tab
      className="doc-tabs"
      menu={{ attached: false, tabular: false }}
      panes={panes.map((p) => ({
        menuItem: {
          key: p.menuItem,
          content: p.menuItem,
          disabled: !p.hasContent,
          // BL-0158: expose the state, not only draw it. WCAG 1.4.3 exempts an
          // inactive control from contrast only when it is marked inactive.
          'aria-disabled': !p.hasContent,
        },
        render: p.render,
      }))}
      defaultActiveIndex={defaultActiveIndex}
    />
  );
};

export default DocumentTabs;
